import React, { useState, useEffect, useCallback } from "react";
import { Sidebar } from "../../components/Sidebar";
import { useAuth } from "react-oidc-context";
import { LoadingState, ErrorState } from "../../components/UI";

const API_URL = process.env.REACT_APP_API_URL || "/api";

const AccessManagement = () => {
  const auth = useAuth();
  const token = auth.user?.access_token;

  const [users, setUsers] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [features, setFeatures] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeTab, setActiveTab] = useState("hotels");

  const [selectedHotel, setSelectedHotel] = useState(null);
  const [hotelSubscriptions, setHotelSubscriptions] = useState([]);
  const [hotelUsers, setHotelUsers] = useState([]);
  const [loadingHotelData, setLoadingHotelData] = useState(false);
  const [featureActionFId, setFeatureActionFId] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);
  const [userHotels, setUserHotels] = useState([]);
  const [loadingUserHotels, setLoadingUserHotels] = useState(false);
  const [userAccessSavingHId, setUserAccessSavingHId] = useState(null);

  const [hotelSearchTerm, setHotelSearchTerm] = useState("");
  const [userSearchTerm, setUserSearchTerm] = useState("");

  const authHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token]
  );

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const fetchInitialData = useCallback(async () => {
    try {
      setInitialLoading(true);
      setError(null);
      const [usersRes, hotelsRes, featuresRes] = await Promise.all([
        fetch(`${API_URL}/luniapp/users`, { headers: authHeaders() }),
        fetch(`${API_URL}/luniapp/hotels`, { headers: authHeaders() }),
        fetch(`${API_URL}/luniapp/features`, { headers: authHeaders() }),
      ]);
      const usersData = await usersRes.json();
      const hotelsData = await hotelsRes.json();
      const featuresData = await featuresRes.json();
      if (usersData.success === "OK") setUsers(usersData.users || []);
      if (hotelsData.success === "OK") setHotels(hotelsData.hotels || []);
      if (featuresData.success === "OK") setFeatures(featuresData.features || []);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setInitialLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (token) fetchInitialData();
  }, [token, fetchInitialData]);

  const fetchHotelUsers = useCallback(
    async (hId) => {
      const usersRes = await fetch(`${API_URL}/luniapp/hotels/${hId}/users`, { headers: authHeaders() });
      const usersData = await usersRes.json();
      if (usersData.success === "OK") {
        setHotelUsers(usersData.users || []);
      }
    },
    [authHeaders]
  );

  const loadHotelData = useCallback(
    async (hotel) => {
      if (!hotel) return;
      setSelectedHotel(hotel);
      setHotelSubscriptions([]);
      setHotelUsers([]);
      setLoadingHotelData(true);
      try {
        const subsRes = await fetch(`${API_URL}/luniapp/hotels/${hotel.hId}/subscriptions`, {
          headers: authHeaders(),
        });
        const subsData = await subsRes.json();
        if (subsData.success === "OK") {
          setHotelSubscriptions(subsData.features || subsData.subscriptions || []);
        }
        await fetchHotelUsers(hotel.hId);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoadingHotelData(false);
      }
    },
    [authHeaders, fetchHotelUsers]
  );

  const toggleHotelFeature = async (fId, isSubscribed) => {
    if (!selectedHotel || featureActionFId !== null) return;

    const previousSubscriptions = hotelSubscriptions;
    if (isSubscribed) {
      setHotelSubscriptions((prev) => prev.filter((s) => s.fId !== fId));
    } else {
      const feature = features.find((f) => f.fId === fId);
      setHotelSubscriptions((prev) => [...prev, { fId, fName: feature?.fName }]);
    }

    const method = isSubscribed ? "DELETE" : "POST";
    const url = `${API_URL}/luniapp/hotels/${selectedHotel.hId}/features/${fId}`;

    setFeatureActionFId(fId);
    try {
      const res = await fetch(url, { method, headers: authHeaders() });
      const data = await res.json();
      if (data.success !== "OK") {
        setHotelSubscriptions(previousSubscriptions);
        setError(data.message || "Erreur lors de la mise à jour");
        return;
      }
      showSuccess(isSubscribed ? "Feature retirée" : "Feature activée");
      try {
        await fetchHotelUsers(selectedHotel.hId);
      } catch (err) {
        console.error(err);
      }
    } catch (err) {
      setHotelSubscriptions(previousSubscriptions);
      setError(err.message);
    } finally {
      setFeatureActionFId(null);
    }
  };

  const loadUserHotels = useCallback(
    async (user, { showLoader = true } = {}) => {
      if (!user) return;
      setSelectedUser(user);
      if (showLoader) {
        setUserHotels([]);
        setLoadingUserHotels(true);
      }
      try {
        const res = await fetch(`${API_URL}/luniapp/users/${user.uId}/hotels`, { headers: authHeaders() });
        const data = await res.json();
        if (data.success === "OK") setUserHotels(data.hotels || []);
      } catch (err) {
        setError(err.message);
      } finally {
        if (showLoader) setLoadingUserHotels(false);
      }
    },
    [authHeaders]
  );

  const saveUserHotelAccess = async (hId, access, isNew) => {
    if (!selectedUser || userAccessSavingHId !== null) return;

    const previousHotels = userHotels;
    const hotelMeta = hotels.find((h) => h.hId === hId);
    const optimisticHotel = {
      hId,
      hName: hotelMeta?.hName ?? `Hôtel #${hId}`,
      hCity: hotelMeta?.hCity ?? null,
      ...access,
    };

    if (isNew) {
      setUserHotels((prev) => [...prev, optimisticHotel]);
    } else {
      setUserHotels((prev) => prev.map((h) => (h.hId === hId ? { ...h, ...access } : h)));
    }

    const url = `${API_URL}/luniapp/users/${selectedUser.uId}/hotels/${hId}/access`;
    setUserAccessSavingHId(hId);
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(access),
      });
      const data = await res.json();
      if (data.success !== "OK") {
        setUserHotels(previousHotels);
        setError(data.message || "Erreur");
        return;
      }
      showSuccess("Accès enregistré");
      await loadUserHotels(selectedUser, { showLoader: false });
    } catch (err) {
      setUserHotels(previousHotels);
      setError(err.message);
    } finally {
      setUserAccessSavingHId(null);
    }
  };

  const removeUserHotel = async (hId) => {
    if (!selectedUser || !window.confirm("Retirer l'accès à cet hôtel ?")) return;
    if (userAccessSavingHId !== null) return;

    const previousHotels = userHotels;
    setUserHotels((prev) => prev.filter((h) => h.hId !== hId));

    setUserAccessSavingHId(hId);
    try {
      const res = await fetch(`${API_URL}/luniapp/users/${selectedUser.uId}/hotels/${hId}/access`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const data = await res.json();
      if (data.success !== "OK") {
        setUserHotels(previousHotels);
        setError(data.message || "Erreur");
        return;
      }
      showSuccess("Accès supprimé");
    } catch (err) {
      setUserHotels(previousHotels);
      setError(err.message);
    } finally {
      setUserAccessSavingHId(null);
    }
  };

  const filteredHotels = hotels.filter((h) =>
    `${h.hName} ${h.hCity || ""}`.toLowerCase().includes(hotelSearchTerm.toLowerCase())
  );
  const filteredUsers = users.filter((u) =>
    `${u.uName} ${u.uSurname} ${u.uMail}`.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const subscribedFIds = new Set(hotelSubscriptions.map((s) => s.fId));

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 p-6 max-w-6xl overflow-auto">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Gestion des accès LuniApp</h1>
        <p className="text-sm text-gray-600 mb-6">
          Hôtels ↔ features (abonnements) et utilisateurs ↔ hôtels. Les droits de lecture par feature sont
          synchronisés automatiquement.
        </p>

        {error && (
          <div className="mb-4">
            <ErrorState message={error} onRetry={() => { setError(null); fetchInitialData(); }} />
          </div>
        )}
        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 text-green-800 rounded border border-green-200 text-sm">
            {successMessage}
          </div>
        )}

        {initialLoading ? (
          <LoadingState embedded message="Chargement…" />
        ) : (
          <>
            <div className="flex gap-2 mb-6 border-b border-gray-200">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  activeTab === "hotels" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-600"
                }`}
                onClick={() => setActiveTab("hotels")}
              >
                Hôtels
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  activeTab === "users" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-600"
                }`}
                onClick={() => setActiveTab("users")}
              >
                Utilisateurs
              </button>
            </div>

            {activeTab === "hotels" && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 bg-white rounded-lg border border-gray-200 p-4">
                  <input
                    type="search"
                    placeholder="Rechercher un hôtel…"
                    className="w-full mb-3 px-3 py-2 border border-gray-300 rounded text-sm"
                    value={hotelSearchTerm}
                    onChange={(e) => setHotelSearchTerm(e.target.value)}
                  />
                  <ul className="max-h-[32rem] overflow-y-auto divide-y divide-gray-100">
                    {filteredHotels.map((h) => (
                      <li key={h.hId}>
                        <button
                          type="button"
                          className={`w-full text-left px-2 py-2 text-sm hover:bg-gray-50 rounded ${
                            selectedHotel?.hId === h.hId ? "bg-blue-50 text-blue-800 font-medium" : ""
                          }`}
                          onClick={() => loadHotelData(h)}
                        >
                          {h.hName}
                          {h.hCity ? <span className="text-gray-500"> — {h.hCity}</span> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="lg:col-span-2 space-y-6 relative min-h-[12rem]">
                  {!selectedHotel ? (
                    <p className="text-gray-500 text-sm">Sélectionnez un hôtel.</p>
                  ) : (
                    <>
                      {loadingHotelData && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70">
                          <LoadingState embedded message="Chargement de l'hôtel…" />
                        </div>
                      )}
                      <section className="bg-white rounded-lg border border-gray-200 p-4">
                        <h2 className="text-lg font-medium mb-3">{selectedHotel.hName} — Features</h2>
                        <ul className="space-y-2">
                          {features.map((f) => {
                            const on = subscribedFIds.has(f.fId);
                            const busy = featureActionFId === f.fId;
                            return (
                              <li key={f.fId} className="flex items-center justify-between py-1">
                                <span className="text-sm">{f.fName}</span>
                                <button
                                  type="button"
                                  disabled={busy || featureActionFId !== null}
                                  className={`text-xs px-3 py-1 rounded disabled:opacity-50 ${
                                    on
                                      ? "bg-red-100 text-red-800 hover:bg-red-200"
                                      : "bg-blue-100 text-blue-800 hover:bg-blue-200"
                                  }`}
                                  onClick={() => toggleHotelFeature(f.fId, on)}
                                >
                                  {busy ? "…" : on ? "Désactiver" : "Activer"}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </section>

                      <section className="bg-white rounded-lg border border-gray-200 p-4">
                        <h2 className="text-lg font-medium mb-3">Utilisateurs liés</h2>
                        {hotelUsers.length === 0 ? (
                          <p className="text-sm text-gray-500">Aucun utilisateur.</p>
                        ) : (
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-gray-500 border-b">
                                <th className="pb-2">Nom</th>
                                <th className="pb-2">Lecture</th>
                                <th className="pb-2">Pages (estim.)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {hotelUsers.map((u) => (
                                <tr key={u.uId} className="border-b border-gray-50">
                                  <td className="py-2">
                                    {u.uName} {u.uSurname}
                                    <div className="text-xs text-gray-500">{u.uMail}</div>
                                  </td>
                                  <td className="py-2">{u.ahRead ? "Oui" : "Non"}</td>
                                  <td className="py-2">{u.pageCount ?? "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </section>
                    </>
                  )}
                </div>
              </div>
            )}

            {activeTab === "users" && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 bg-white rounded-lg border border-gray-200 p-4">
                  <input
                    type="search"
                    placeholder="Rechercher un utilisateur…"
                    className="w-full mb-3 px-3 py-2 border border-gray-300 rounded text-sm"
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                  />
                  <ul className="max-h-[32rem] overflow-y-auto divide-y divide-gray-100">
                    {filteredUsers.map((u) => (
                      <li key={u.uId}>
                        <button
                          type="button"
                          className={`w-full text-left px-2 py-2 text-sm hover:bg-gray-50 rounded ${
                            selectedUser?.uId === u.uId ? "bg-blue-50 text-blue-800 font-medium" : ""
                          }`}
                          onClick={() => loadUserHotels(u)}
                        >
                          {u.uName} {u.uSurname}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="lg:col-span-2 relative min-h-[12rem]">
                  {!selectedUser ? (
                    <p className="text-gray-500 text-sm">Sélectionnez un utilisateur.</p>
                  ) : (
                    <>
                      {loadingUserHotels && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70">
                          <LoadingState embedded message="Chargement…" />
                        </div>
                      )}
                      <section className="bg-white rounded-lg border border-gray-200 p-4">
                        <h2 className="text-lg font-medium mb-3">
                          {selectedUser.uName} {selectedUser.uSurname} — Hôtels
                        </h2>

                        <div className="mb-4 p-3 bg-gray-50 rounded text-sm">
                          <p className="font-medium mb-2">Ajouter un hôtel</p>
                          <AddHotelAccessForm
                            hotels={hotels}
                            existingHIds={new Set(userHotels.map((h) => h.hId))}
                            disabled={userAccessSavingHId !== null}
                            onAdd={(hId, access) => saveUserHotelAccess(hId, access, true)}
                          />
                        </div>

                        {userHotels.length === 0 ? (
                          <p className="text-sm text-gray-500">Aucun hôtel associé.</p>
                        ) : (
                          <ul className="space-y-3">
                            {userHotels.map((h) => (
                              <li
                                key={h.hId}
                                className={`border border-gray-100 rounded p-3 ${
                                  userAccessSavingHId === h.hId ? "opacity-60" : ""
                                }`}
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <span className="font-medium text-sm">
                                    {h.hName}
                                    {h.hCity ? ` (${h.hCity})` : ""}
                                  </span>
                                  <button
                                    type="button"
                                    className="text-xs text-red-600 hover:underline disabled:opacity-50"
                                    disabled={userAccessSavingHId !== null}
                                    onClick={() => removeUserHotel(h.hId)}
                                  >
                                    Retirer
                                  </button>
                                </div>
                                <HotelAccessFlags
                                  access={h}
                                  disabled={userAccessSavingHId !== null}
                                  onSave={(access) => saveUserHotelAccess(h.hId, access, false)}
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

function HotelAccessFlags({ access, onSave, disabled }) {
  const [flags, setFlags] = useState({
    ahRead: !!access.ahRead,
    ahModify: !!access.ahModify,
    ahDelete: !!access.ahDelete,
    ahGiveAccessHotel: !!access.ahGiveAccessHotel,
  });

  useEffect(() => {
    setFlags({
      ahRead: !!access.ahRead,
      ahModify: !!access.ahModify,
      ahDelete: !!access.ahDelete,
      ahGiveAccessHotel: !!access.ahGiveAccessHotel,
    });
  }, [access]);

  return (
    <div className="flex flex-wrap gap-3 text-sm items-center">
      {[
        ["ahRead", "Lecture"],
        ["ahModify", "Modification"],
        ["ahDelete", "Suppression"],
        ["ahGiveAccessHotel", "Gérer accès hôtel"],
      ].map(([key, label]) => (
        <label key={key} className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={flags[key]}
            disabled={disabled}
            onChange={(e) => setFlags((f) => ({ ...f, [key]: e.target.checked }))}
          />
          {label}
        </label>
      ))}
      <button
        type="button"
        className="ml-auto text-xs px-2 py-1 bg-gray-800 text-white rounded disabled:opacity-50"
        disabled={disabled}
        onClick={() => onSave(flags)}
      >
        Enregistrer
      </button>
    </div>
  );
}

function AddHotelAccessForm({ hotels, existingHIds, onAdd, disabled }) {
  const [hId, setHId] = useState("");
  const available = hotels.filter((h) => !existingHIds.has(h.hId));

  return (
    <div className="flex flex-wrap gap-2 items-end">
      <select
        className="border border-gray-300 rounded px-2 py-1 text-sm min-w-[200px] disabled:opacity-50"
        value={hId}
        disabled={disabled}
        onChange={(e) => setHId(e.target.value)}
      >
        <option value="">— Hôtel —</option>
        {available.map((h) => (
          <option key={h.hId} value={h.hId}>
            {h.hName}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="text-xs px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
        disabled={!hId || disabled}
        onClick={() => {
          onAdd(Number(hId), { ahRead: true, ahModify: false, ahDelete: false, ahGiveAccessHotel: false });
          setHId("");
        }}
      >
        Ajouter (lecture)
      </button>
    </div>
  );
}

export default AccessManagement;
